import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  diagramApi,
  type DiagramWithUrls,
  type CreateDiagramInput,
  type UpdateDiagramMetaInput,
  type CompilePageInput,
} from '../../../services/diagram';
import type { DiagramResponse } from '@xdface-vault/shared';

const diagramKeys = {
  all: ['diagrams'] as const,
  lists: () => [...diagramKeys.all, 'list'] as const,
  details: () => [...diagramKeys.all, 'detail'] as const,
  detail: (id: string) => [...diagramKeys.details(), id] as const,
};

// List all diagrams for the current user
export function useDiagramList() {
  return useQuery({
    queryKey: diagramKeys.lists(),
    queryFn: () => diagramApi.listDiagrams(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get a single diagram (metadata + presigned URLs)
export function useDiagram(id: string | undefined) {
  return useQuery({
    queryKey: diagramKeys.detail(id!),
    queryFn: () => diagramApi.getDiagram(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// Create a new diagram with optimistic update
export function useCreateDiagram() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateDiagramInput) => diagramApi.createDiagram(input),
    onMutate: async (newDiagram) => {
      await queryClient.cancelQueries({ queryKey: diagramKeys.lists() });
      const previousData = queryClient.getQueryData<DiagramResponse[]>(diagramKeys.lists());

      const optimistic: DiagramWithUrls = {
        mxfileUrl: '',
        uploadUrl: '',
        irUrl: '',
        id: `optimistic-${Date.now()}`,
        userId: 'me',
        title: newDiagram.title,
        tags: newDiagram.tags ?? [],
        s3Key: '',
        pageCount: 1,
        kits: [newDiagram.kit],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 1,
      };

      queryClient.setQueryData<DiagramResponse[]>(diagramKeys.lists(), (old) => [
        optimistic,
        ...(old ?? []),
      ]);

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(diagramKeys.lists(), context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: diagramKeys.lists() });
    },
  });
}

export function useSaveDiagram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      mxfile,
      expectedVersion,
    }: {
      id: string;
      mxfile: string;
      expectedVersion: number;
    }) => diagramApi.saveMxfile(id, mxfile, expectedVersion),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: diagramKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: diagramKeys.lists() });
    },
  });
}

// Compile+persist one page from IR (used by editor save)
export function useCompileDiagramPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CompilePageInput }) =>
      diagramApi.compilePage(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: diagramKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: diagramKeys.lists() });
    },
  });
}

// Update diagram metadata
export function useUpdateDiagramMeta() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDiagramMetaInput }) =>
      diagramApi.updateDiagramMeta(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: diagramKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: diagramKeys.lists() });
    },
  });
}

// Delete diagram with optimistic update
export function useDeleteDiagram() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => diagramApi.deleteDiagram(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: diagramKeys.lists() });
      const previousData = queryClient.getQueryData<DiagramResponse[]>(diagramKeys.lists());

      queryClient.setQueryData<DiagramResponse[]>(diagramKeys.lists(), (old) =>
        (old ?? []).filter((d) => d.id !== id),
      );

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(diagramKeys.lists(), context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: diagramKeys.lists() });
    },
  });
}
